/**
 * cloudinaryUrl — synthesize a narrower variant of a Cloudinary asset
 * by injecting transformation parameters into the URL path. Returns the
 * input unchanged if it's not a Cloudinary URL (so we don't break Base64
 * previews in dev or arbitrary external URLs).
 *
 * Cloudinary URLs look like:
 *   https://res.cloudinary.com/{cloud}/image/upload/{base_transforms}/{public_id}.{ext}
 *
 * We inject `w_{width},c_fill,q_auto,f_auto` right after `/upload/` so
 * the browser only ever downloads the resolution it actually needs.
 * The original transformation chain (the one applied at upload) is
 * preserved — Cloudinary chains transformations left-to-right.
 *
 * `crop`: 'fill' centres the subject (smart enough for food photos
 * because the source was already cropped by `ImageCropperModal`).
 *         'limit' = never upscale, just cap (use for full-bleed previews).
 */
const CLOUDINARY_PREFIX = '/image/upload/';

export function cloudinaryUrl(src, { w, h, crop = 'fill', q = 'auto', f = 'auto' } = {}) {
  if (!src || typeof src !== 'string') return src;
  if (!src.includes('res.cloudinary.com')) return src;
  const idx = src.indexOf(CLOUDINARY_PREFIX);
  if (idx === -1) return src;

  const parts = [];
  if (w) parts.push(`w_${w}`);
  if (h) parts.push(`h_${h}`);
  if (crop) parts.push(`c_${crop}`);
  if (q) parts.push(`q_${q}`);
  if (f) parts.push(`f_${f}`);
  if (parts.length === 0) return src;

  const before = src.slice(0, idx + CLOUDINARY_PREFIX.length);
  const after = src.slice(idx + CLOUDINARY_PREFIX.length);
  return `${before}${parts.join(',')}/${after}`;
}

/**
 * Build a responsive `srcSet` string for a Cloudinary image.
 *   srcSetFor(url, [240, 480, 720, 1080]) →
 *     "url-w_240 240w, url-w_480 480w, ..."
 *
 * Returns `null` for non-Cloudinary sources so the calling component can
 * skip the `srcSet` attribute entirely.
 */
export function srcSetFor(src, widths, opts = {}) {
  if (!src || !src.includes('res.cloudinary.com')) return null;
  return widths
    .map((w) => `${cloudinaryUrl(src, { ...opts, w })} ${w}w`)
    .join(', ');
}
